import { useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';

type Result = {
	trackChange: (name: keyof FormValues) => void;
	trackFocus: (name: keyof FormValues) => void;
	trackGdprConsent: (consentValue: boolean) => void;
	trackPublic: (isPublic: boolean) => void;
	trackEditProfile: () => void;
	trackGeolocPreviewEditClick: () => void;
	trackShareInfoWithOrganizationShown: () => void;
	trackShareInfoWithOrganizationUncheck: () => void;
};

const FOCUS_EVENT_MAP: Partial<Record<keyof FormValues, string>> = {
	firstName: 'sign_form_first_name_focus',
	lastName: 'sign_form_last_name_focus',
	email: 'sign_form_email_focus',
	address: 'sign_form_address_focus',
};
const CHANGE_EVENT_MAP: Partial<Record<keyof FormValues, string>> = {
	firstName: 'sign_form_first_name_input',
	lastName: 'sign_form_last_name_input',
	email: 'sign_form_email_input',
	streetAddress: 'geo_profile_street_address_change',
	city: 'geo_profile_city_change',
	countryCode: 'geo_profile_country_code_change',
	postalCode: 'geo_profile_post_code_change',
};

export function useFieldInteractionTracking(): Result {
	const {
		data: { petition: details },
	} = usePetitionDetailsPageData();
	const track = useTracking();

	return useMemo(
		() => ({
			trackChange: (name: keyof FormValues) => {
				const eventName = CHANGE_EVENT_MAP[name];
				if (!eventName) return;
				void track(eventName);
			},
			trackFocus: (name: keyof FormValues) => {
				const eventName = FOCUS_EVENT_MAP[name];
				if (!eventName) return;
				void track(eventName);
			},
			trackGdprConsent: (consentValue: boolean) => {
				void track(consentValue ? 'consent_optin_yes_click' : 'consent_optin_no_click');
			},
			trackPublic: (isPublic: boolean) => {
				void track('click_display_my_info_optin', { is_public: isPublic });
			},
			trackEditProfile: () => {
				void track('sign_edit_profile', { petition_id: details.id });
			},
			trackGeolocPreviewEditClick: () => {
				void track('sign_form_geoloc_preview_edit_click', { petition_id: details.id });
			},
			trackShareInfoWithOrganizationShown: () => {
				void track('sign_form_npo_contact_consent_shown');
			},
			trackShareInfoWithOrganizationUncheck: () => {
				void track('sign_form_npo_contact_consent_uncheck');
			},
		}),
		[track, details],
	);
}
